import { z } from 'zod';

/**
 * The AI provider catalog — the single place a provider is *described*.
 *
 * WHY THIS FILE EXISTS AND WHY IT IS IN `contracts`.
 *
 * "Make it dynamic so other providers are easy to add later" is an architectural
 * requirement, not a compliment. Concretely it means: adding Anthropic, Gemini or
 * a local gateway must be *registering a module*, not editing an enum here, a
 * `switch` there, and a hardcoded "OpenAI" in the settings screen. The dashboard
 * therefore renders from what the server sends, and what the server sends comes
 * from this file.
 *
 * It lives in `contracts` rather than in `packages/ai` for one hard reason:
 * `apps/api` does not depend on `packages/ai` and must not start. That package
 * contains `decryptApiKey` and the OpenAI SDK, and the whole BYOK design rests on
 * the API process being *structurally incapable* of turning a stored row back
 * into a usable key. Provider metadata is wire format — the API serves it, the
 * dashboard draws it — so it belongs in the package both already depend on, and
 * the decryptor stays on the other side of a package boundary.
 *
 * WHAT IS *NOT* HERE: anything that can make a model call. This file is data plus
 * Zod. `packages/ai/src/registry.ts` binds each id to a factory that can, and a
 * test asserts the two sides list the same ids — so a provider that is described
 * but not implemented fails the build instead of shipping a settings screen that
 * offers a key field leading nowhere.
 */

// ---------------------------------------------------------------------------
// Provider ids
// ---------------------------------------------------------------------------

/**
 * Every provider the platform knows about, as a tuple so `z.enum` can consume it.
 *
 * DECISION: this stays a closed enum rather than becoming a free-form string
 * validated against the catalog at runtime.
 *
 * The trade-off is real — a new provider means editing this tuple, so `contracts`
 * has to be touched — but the alternative buys nothing, because the *binding*
 * constraint is one layer down: `ai_provider_settings.provider` and
 * `ai_requests.provider` are PostgreSQL enum columns. A free-form string in
 * TypeScript would still be rejected by Postgres until a migration added the
 * label, so "no code change needed" was never on the table. Given that a
 * migration is unavoidable, the enum is strictly better: exhaustiveness checking
 * at every `switch`, autocompletion, and a compile error — rather than a 500 at
 * runtime — when a provider id is misspelled at an API boundary.
 *
 * Adding a provider therefore touches exactly four places, and no others:
 *   1. this tuple + its catalog entry below,
 *   2. `packages/ai/src/<name>-provider.ts` (the implementation),
 *   3. `packages/ai/src/registry.ts` (one line binding id → factory),
 *   4. the Prisma `AiProvider` enum + a migration.
 * There is no fifth. In particular there is no UI change and no API change: the
 * settings screen is drawn from this catalog and the key check is driven by the
 * probe descriptor below.
 */
export const AI_PROVIDER_IDS = ['OPENAI', 'MIDIAI'] as const;
export type AiProviderId = (typeof AI_PROVIDER_IDS)[number];

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/**
 * What a provider (or a specific model) can be used *for* in this product.
 *
 * The dashboard needs this to answer "can I pick this model for comment
 * generation?" without knowing anything about the provider, and the scoring and
 * comment paths need it to refuse a model that cannot do the job rather than
 * discovering it in a 400 halfway through a batch.
 */
export const AI_CAPABILITIES = [
  'POST_SCORING',
  'COMMENT_GENERATION',
  /**
   * Reading a timestamp off a picture of one.
   *
   * Listed as a capability rather than assumed, because it is the only one that
   * needs the model to see an image. A text-only model registered tomorrow would
   * otherwise be picked for the time-extraction fallback and fail on every
   * single call — and it would fail *quietly*, since a post with no `postedAt`
   * looks exactly like a post the parser could not read.
   */
  'POST_TIME_EXTRACTION',
] as const;
export const aiCapabilitySchema = z.enum(AI_CAPABILITIES);
export type AiCapability = (typeof AI_CAPABILITIES)[number];

/**
 * Rough price/quality band, used only for ordering and for a badge in the picker.
 *
 * Deliberately not a price: real prices change often enough that a number
 * compiled into the frontend would be wrong within weeks, and a stale price shown
 * next to a "spend my money" button is worse than no price at all.
 */
export const AI_MODEL_TIERS = ['ECONOMY', 'BALANCED', 'PREMIUM'] as const;
export const aiModelTierSchema = z.enum(AI_MODEL_TIERS);
export type AiModelTier = (typeof AI_MODEL_TIERS)[number];

export const aiModelDescriptorSchema = z.object({
  /** Sent verbatim to the provider. */
  id: z.string().min(1),
  /** Vietnamese-facing label for the picker. */
  label: z.string().min(1),
  capabilities: z.array(aiCapabilitySchema).min(1),
  tier: aiModelTierSchema,
});
export type AiModelDescriptor = z.infer<typeof aiModelDescriptorSchema>;

// ---------------------------------------------------------------------------
// Key shape hints
// ---------------------------------------------------------------------------

/**
 * Enough for the form to help the user, and never enough to reject them.
 *
 * `prefix` is ADVISORY. The server does not enforce it, and must not: provider
 * key formats change without notice (OpenAI alone has shipped `sk-`, `sk-proj-`
 * and org-scoped variants), and a prefix check that is one release out of date
 * rejects a key that works perfectly. The authoritative answer to "is this key
 * good?" comes from calling the provider — see {@link AiKeyProbe}. The UI may
 * show a soft warning on a mismatch; it may not block submission.
 */
export const aiApiKeyHintSchema = z.object({
  /** Shown as "keys usually start with…". Null when the provider has no stable prefix. */
  prefix: z.string().nullable(),
  /** Masked example for the input placeholder. Contains no real key material. */
  placeholder: z.string().min(1),
  minLength: z.number().int().positive(),
  maxLength: z.number().int().positive(),
});
export type AiApiKeyHint = z.infer<typeof aiApiKeyHintSchema>;

// ---------------------------------------------------------------------------
// The key probe
// ---------------------------------------------------------------------------

/**
 * How to ask a provider "is this key usable?" using nothing but `fetch`.
 *
 * THIS IS THE PIECE THAT MAKES THE DESIGN ACTUALLY DYNAMIC. A key check written
 * as a branch in the API (`if (provider === 'OPENAI') …`) means every new
 * provider needs API work before its key can be verified — and, historically,
 * means the check quietly gets skipped for the new provider. Expressed as data,
 * the check comes along with the catalog entry: register Anthropic with
 * `authHeader: 'x-api-key'`, `authPrefix: ''` and its version header, and key
 * validation works on the first run with no code change.
 *
 * It is a `fetch` descriptor rather than an SDK call for the same reason the rest
 * of this file avoids `packages/ai`: pulling a provider SDK into `apps/api` would
 * put a completion client inside a process that has no business making model
 * calls, and would drag the decryptor in behind it.
 *
 * `{model}` in `path` is substituted with the model being configured. Probing the
 * *specific* model rather than just listing models proves three things in one
 * request — the key authenticates, the base URL is right, and this account can
 * actually reach the model the workspace just chose — which is precisely the set
 * of things that otherwise fail silently hours later.
 */
export const aiKeyProbeSchema = z.object({
  method: z.enum(['GET', 'POST']),
  /** Appended to the base URL. May contain the `{model}` placeholder. */
  path: z.string().min(1),
  /** Header the key travels in. */
  authHeader: z.string().min(1),
  /** Prefix in front of the key, e.g. `Bearer `. Empty string for raw-key headers. */
  authPrefix: z.string(),
  /** Static headers the provider requires, e.g. an API version pin. */
  headers: z.record(z.string()),
  /**
   * Non-2xx statuses that nevertheless prove the key works.
   *
   * The default mapping (2xx → VALID, 404 → MODEL_NOT_AVAILABLE) assumes the
   * probe addresses a *model*, which is true of any provider that has models to
   * address. A queue-based provider has none: the only endpoint that both
   * authenticates and costs nothing is a job lookup, and a lookup for a job id
   * that cannot exist answers 404 precisely *because* the key was accepted and
   * scoped to its own jobs. Reporting that as "model không khả dụng" would tell
   * a user to change a setting the provider does not have.
   *
   * Listing the statuses here rather than branching in the API keeps the promise
   * the catalog makes: a provider registered tomorrow gets a working key check
   * today, with no edit to `provider-key-probe.ts`.
   */
  successStatuses: z.array(z.number().int()).default([]),
});
export type AiKeyProbe = z.infer<typeof aiKeyProbeSchema>;

// ---------------------------------------------------------------------------
// The provider descriptor
// ---------------------------------------------------------------------------

/**
 * Everything the settings screen needs in order to render a provider it has
 * never heard of.
 *
 * The test of this shape is: could a dashboard developer build the whole screen
 * knowing only this type, with no `if (provider === 'OPENAI')` anywhere? That is
 * why `docsUrl` and `keyHint` are here — a user who cannot find where to get a
 * key does not configure one, and "go get a key" is provider-specific knowledge
 * that would otherwise be hardcoded in the frontend.
 */
export const aiProviderDescriptorSchema = z.object({
  id: z.enum(AI_PROVIDER_IDS),
  /** Brand name as the provider writes it. Not translated. */
  label: z.string().min(1),
  /** One line of Vietnamese explaining what this provider is, for the picker. */
  description: z.string().min(1),
  /** Where the user goes to obtain a key. Shown as a link next to the input. */
  docsUrl: z.string().url(),
  /** Default API base URL. Shown read-only; not user-editable in this version. */
  baseUrl: z.string().url(),
  keyHint: aiApiKeyHintSchema,
  models: z.array(aiModelDescriptorSchema).min(1),
  /** Must be one of `models[].id`; enforced by a test, not by this schema. */
  defaultModel: z.string().min(1),
  /** Union of the capabilities of its models. */
  capabilities: z.array(aiCapabilitySchema).min(1),
  keyProbe: aiKeyProbeSchema,
  /**
   * How many keys this provider's credential may hold, and therefore whether the
   * settings screen draws one input or a textarea.
   *
   * WHY THIS IS A NUMBER AND NOT A BOOLEAN. It pairs with
   * {@link AiProviderDescriptor.maxConcurrentCallsPerKey} to express the real
   * shape of a provider's throughput. A provider that admits one call per key at
   * a time is not "slow" — it is *serial*, and the only way to run two jobs at
   * once against it is to hold two keys. A workspace that pastes six keys is
   * buying six lanes, and the pool scheduler needs to know that this is a
   * deliberate configuration rather than six copies of the same credential.
   */
  maxKeys: z.number().int().positive(),
  /**
   * In-flight calls a single key may have. `1` means the key is a lane, not a
   * credential: the worker must lease it, call, and release it before any other
   * job may use it.
   */
  maxConcurrentCallsPerKey: z.number().int().positive(),
  /**
   * Whether this provider can be sent images alongside the prompt.
   *
   * Gates the time-extraction path. Stated per provider rather than inferred
   * from the model name, because "does gpt-4o see pictures?" is exactly the kind
   * of fact that a string comparison gets wrong the week a new model ships.
   */
  supportsImages: z.boolean(),
  /**
   * How a call is made: one request that returns the answer, or a submit that
   * returns a job id and a poll loop that waits for it.
   *
   * The dashboard uses this only to set expectations ("nhà cung cấp này xếp hàng,
   * một câu trả lời có thể mất vài phút"); the runtime binding lives in
   * `packages/ai/src/registry.ts`. It is here because a user staring at a draft
   * that has not appeared deserves to know whether that is normal.
   */
  transport: z.enum(['SYNCHRONOUS', 'QUEUED']),
});
export type AiProviderDescriptor = z.infer<typeof aiProviderDescriptorSchema>;

// ---------------------------------------------------------------------------
// The catalog itself
// ---------------------------------------------------------------------------

/**
 * `satisfies Record<AiProviderId, …>` is doing real work: it makes the compiler
 * reject a provider added to {@link AI_PROVIDER_IDS} without an entry here, and
 * an entry here whose key is not in the tuple. The two can never drift.
 */
export const AI_PROVIDER_CATALOG = {
  OPENAI: {
    id: 'OPENAI',
    label: 'OpenAI',
    description: 'Nhà cung cấp mặc định. Hỗ trợ chấm điểm bài viết và soạn bình luận.',
    docsUrl: 'https://platform.openai.com/api-keys',
    baseUrl: 'https://api.openai.com/v1',
    keyHint: {
      prefix: 'sk-',
      placeholder: 'sk-proj-••••••••••••••••••••',
      minLength: 20,
      maxLength: 500,
    },
    models: [
      {
        id: 'gpt-4o-mini',
        label: 'GPT-4o mini — rẻ, đủ dùng cho chấm điểm',
        capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
        tier: 'ECONOMY',
      },
      {
        id: 'gpt-4o',
        label: 'GPT-4o — chính xác hơn, đắt hơn',
        capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
        tier: 'BALANCED',
      },
      {
        id: 'gpt-4.1-mini',
        label: 'GPT-4.1 mini — cân bằng giá và chất lượng',
        capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
        tier: 'BALANCED',
      },
      {
        id: 'gpt-4.1',
        label: 'GPT-4.1 — chất lượng cao nhất',
        capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
        tier: 'PREMIUM',
      },
    ],
    defaultModel: 'gpt-4o-mini',
    capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
    keyProbe: {
      method: 'GET',
      // Retrieving the chosen model, not `/models`: it authenticates the key AND
      // proves this account can reach that exact model, in one cheap call.
      path: '/models/{model}',
      authHeader: 'Authorization',
      authPrefix: 'Bearer ',
      headers: {},
      successStatuses: [],
    },
    maxKeys: 1,
    maxConcurrentCallsPerKey: 8,
    supportsImages: true,
    transport: 'SYNCHRONOUS',
  },
  MIDIAI: {
    id: 'MIDIAI',
    label: 'MidiAI',
    description:
      'Cổng hỏi–đáp của Miditech. Nhận cả ảnh lẫn văn bản, xếp hàng theo tab trình duyệt nên trả lời chậm hơn nhưng dùng được nhiều khoá song song.',
    docsUrl: 'https://ai.miditech.vn',
    baseUrl: 'https://ai.miditech.vn',
    keyHint: {
      prefix: 'sk_live_',
      placeholder: 'sk_live_••••••••••••••••••••',
      minLength: 20,
      maxLength: 500,
    },
    /**
     * One pseudo-model, because the endpoint takes no model parameter — the
     * upstream model is whatever the operator of the gateway has signed in.
     *
     * It is still listed rather than left empty: `ai_requests.model` is NOT NULL
     * and is the column an operator groups by when asking "what did this cost
     * and who answered it". A row reading `midiai-default` is a truthful answer;
     * an empty string is a hole in the ledger.
     */
    models: [
      {
        id: 'midiai-default',
        label: 'MidiAI — model do quản trị viên cổng chọn',
        capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
        tier: 'BALANCED',
      },
    ],
    defaultModel: 'midiai-default',
    capabilities: ['POST_SCORING', 'COMMENT_GENERATION', 'POST_TIME_EXTRACTION'],
    keyProbe: {
      method: 'GET',
      /**
       * A job id that is syntactically valid and cannot exist.
       *
       * `GET /api/v1/config` is public, so it proves nothing about a key. Submitting
       * a real job would prove everything and cost a queue slot plus quota — for a
       * button a user may press five times while pasting. A lookup is free, and
       * because a key may only read its own jobs, `404 JOB_NOT_FOUND` is itself the
       * proof of authentication: an unknown key never gets that far, it gets 401.
       * Hence `successStatuses: [404]`.
       *
       * `{model}` is deliberately absent — there is no model to name here.
       */
      path: '/api/v1/jobs/00000000-0000-0000-0000-000000000000',
      authHeader: 'Authorization',
      authPrefix: 'Bearer ',
      headers: {},
      successStatuses: [404],
    },
    /**
     * A pool, because the provider is serial per key.
     *
     * 20 is a ceiling against a paste accident (a whole file of secrets dropped
     * into the textarea), not a recommendation. The real limit is how many keys
     * the gateway's administrator issued.
     */
    maxKeys: 20,
    maxConcurrentCallsPerKey: 1,
    supportsImages: true,
    transport: 'QUEUED',
  },
} as const satisfies Record<AiProviderId, AiProviderDescriptor>;

/** The catalog as an array, in `AI_PROVIDER_IDS` order — the order the UI shows. */
export const AI_PROVIDER_DESCRIPTORS: readonly AiProviderDescriptor[] = AI_PROVIDER_IDS.map(
  (id) => AI_PROVIDER_CATALOG[id],
);

export function describeAiProvider(id: AiProviderId): AiProviderDescriptor {
  return AI_PROVIDER_CATALOG[id];
}

export function isAiProviderId(value: string): value is AiProviderId {
  return (AI_PROVIDER_IDS as readonly string[]).includes(value);
}

/**
 * Resolve the model to use for a provider.
 *
 * An unknown model string is **accepted, not rejected**. Providers ship new
 * models constantly, and a hardcoded allow-list would mean a code deploy before a
 * workspace could use a model that was released this morning. The catalog list is
 * a convenience for the picker; the authoritative check is the probe, which asks
 * the provider whether the model exists for this account. `known` lets the UI say
 * "model này không có trong danh sách gợi ý" without blocking anyone.
 */
export function resolveAiModel(
  id: AiProviderId,
  requested: string | null | undefined,
): { model: string; known: boolean } {
  const descriptor = AI_PROVIDER_CATALOG[id];
  const trimmed = requested?.trim();

  if (!trimmed) return { model: descriptor.defaultModel, known: true };

  const known = descriptor.models.some((model) => model.id === trimmed);
  return { model: trimmed, known };
}

/**
 * Substitute `{model}` and join onto the base URL.
 *
 * Both sides are trimmed of slashes so a catalog entry cannot produce `//models`
 * — some gateways treat that as a different route and answer 404, which would be
 * reported to the user as "model không khả dụng" for a perfectly good key.
 */
export function buildAiKeyProbeUrl(id: AiProviderId, model: string): string {
  const descriptor = AI_PROVIDER_CATALOG[id];
  const base = descriptor.baseUrl.replace(/\/+$/, '');
  const path = descriptor.keyProbe.path.replace('{model}', encodeURIComponent(model));

  return `${base}/${path.replace(/^\/+/, '')}`;
}
